import React from "react";
import { Loader2 } from "lucide-react";
import Pagination from "./Pagination";

/**
 * Reusable data table with optional loading, empty, and pagination states.
 *
 * Props:
 *   columns   {Array}   – column definitions:
 *     { header, key, align, className, render(row, idx) }
 *
 *   rows      {Array}   – data rows (each row is passed to render())
 *   loading   {bool}    – show spinner instead of rows
 *   emptyText {string}  – message when rows is empty (default "No data found.")
 *
 *   // Pagination (all required together, omit for no pagination)
 *   page        {number}
 *   totalPages  {number}
 *   onPageChange{fn}
 *   totalCount  {number}  – total items across all pages (for "Showing X–Y of Z")
 *   pageSize    {number}  – items per page (for calculating ranges)
 *
 *   // Footer slot (shown on left of pagination row)
 *   footerLeft  {ReactNode}  – custom left-side footer content
 */
const DataTable = ({
  columns = [],
  rows = [],
  loading = false,
  emptyText = "No data found.",
  page,
  totalPages,
  onPageChange,
  totalCount,
  pageSize,
  footerLeft,
}) => {
  const hasPagination = totalPages && totalPages > 1 && onPageChange;

  // "Showing X–Y of Z" calculation
  let showingText = null;
  if (totalCount != null && page && pageSize) {
    const start = (page - 1) * pageSize + 1;
    const end   = Math.min(page * pageSize, totalCount);
    showingText = loading
      ? "Loading…"
      : `Showing ${start}–${end} of ${totalCount}`;
  } else if (footerLeft) {
    showingText = null; // use footerLeft slot
  }

  const colCount = columns.length;

  return (
    <div className="bg-white border border-surface-border rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          {/* Head */}
          <thead>
            <tr className="border-b border-surface-border">
              {columns.map((col, i) => (
                <th
                  key={i}
                  className={`px-4 py-3.5 text-xs font-semibold text-brand-muted whitespace-nowrap ${
                    col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
                  } ${col.className || ""}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} className="text-center py-16">
                  <Loader2 className="w-5 h-5 text-primary animate-spin mx-auto" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="text-center py-14 text-sm text-brand-muted">
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={row.id ?? idx}
                  className="border-b border-surface-border last:border-0 group"
                >
                  {columns.map((col, ci) => (
                    <td
                      key={ci}
                      className={`${col.noPadding ? "p-0" : "px-4 py-3.5"} group-hover:bg-surface/50 transition-colors ${
                        col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
                      } ${col.cellClassName || ""}`}
                    >
                      {col.render ? col.render(row, idx) : row[col.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {(footerLeft || showingText || hasPagination) && (
        <div className="px-5 py-3.5 border-t border-surface-border flex items-center justify-between gap-4 flex-wrap">
          <p className="text-xs text-brand-muted">
            {footerLeft ?? showingText}
          </p>
          {hasPagination && (
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DataTable;
